import React, { useEffect, useMemo, useRef, useState, memo } from "react";
import { Global_Data } from "../../globalData/GlobalData";
import * as MUIICon from "@mui/icons-material";
import { Button, IconButton } from "@mui/material";
import { Icon } from "../../utils/MuiIcons/Icon";
import ComponentLoadingBoundary from "../Loading/ComponentLoadingBoundary";
import useButtonActionAccess from "./useButtonActionAccess";

const ButtonComponent = ({
  data,
  onClickvalue,
  formName,
  formData,
  formValues,
  isResponseArrived,
  onTerminateSignal
}) => {
  const { token } = Global_Data();

  const retryConfig = data?.data?.sRetry;
  const terminateConfig = data?.data?.sTerminateSignal;
  const terminateEnabled = terminateConfig?.bTerminateAllowed === true;
  const signalMechanism = terminateConfig?.sSignalMechanism;
  const signalID = terminateConfig?.sSignalID;
  const retryEnabled = retryConfig?.bRetryAllowed === true;
  const retryMinutes = Number(retryConfig?.iRetryAfterMin || 0);

  const [retrySecondsLeft, setRetrySecondsLeft] = useState(0);
  const [runtimeSignalID, setRuntimeSignalID] = useState("");
  const accessItems = useMemo(() => [data], [data]);
  const { isAllowed, isLoading } = useButtonActionAccess({
    items: accessItems,
    formName,
    formData,
    formValues,
    token
  });

  const retryIntervalRef = useRef(null);
  const terminateHandledRef = useRef(false);
  const effectiveSignalID = runtimeSignalID || signalID;

  const isRetryActive = retryEnabled && retrySecondsLeft > 0;

  const formatRetryTime = seconds => {
    const safeSeconds = Math.max(Number(seconds) || 0, 0);
    const mins = Math.floor(safeSeconds / 60);
    const secs = safeSeconds % 60;

    return `${mins}:${String(secs).padStart(2, "0")}`;
  };

  const startRetryCountdown = () => {
    if (!retryEnabled || retryMinutes <= 0) {
      return;
    }

    const totalSeconds = retryMinutes * 60;
    setRetrySecondsLeft(totalSeconds);
  };

  const handleButtonClick = event => {
    if (isRetryActive) {
      return;
    }

    if (retryEnabled) {
      startRetryCountdown();
    }

    onClickvalue?.(event);
  };

  useEffect(() => {
    if (retrySecondsLeft <= 0) {
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
      return;
    }

    if (retryIntervalRef.current) {
      clearInterval(retryIntervalRef.current);
    }

    retryIntervalRef.current = setInterval(() => {
      setRetrySecondsLeft(prev => {
        if (prev <= 1) {
          clearInterval(retryIntervalRef.current);
          retryIntervalRef.current = null;
          return 0;
        }

        return prev - 1;
      });
    }, 1000);

    return () => {
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
    };
  }, [retrySecondsLeft]);

  useEffect(() => {
    terminateHandledRef.current = false;
  }, [signalID]);

  useEffect(() => {
    if (!terminateEnabled || !signalID) return;

    const handler = event => {
      const buttonName = event?.detail?.buttonName;
      const resolvedSignalID = event?.detail?.signalID;

      if (buttonName === data?.component?.sName && resolvedSignalID) {
        terminateHandledRef.current = false;
        setRuntimeSignalID(resolvedSignalID);
      }
    };

    window.addEventListener("BITS_BUTTON_SIGNAL_ID_READY", handler);

    return () => {
      window.removeEventListener("BITS_BUTTON_SIGNAL_ID_READY", handler);
    };
  }, [terminateEnabled, signalID, data?.component?.sName]);

  useEffect(() => {
    if (!terminateEnabled || !signalMechanism || !effectiveSignalID) return;

    if (signalMechanism !== "SSE:3PT_Message") return;

    const handler = event => {
      const receivedSignalID = event?.detail?.requestID;

      if (
        receivedSignalID === effectiveSignalID &&
        !terminateHandledRef.current
      ) {
        terminateHandledRef.current = true;
        onTerminateSignal?.();
      }
    };

    window.addEventListener("BITS_3PT_MESSAGE_SIGNAL", handler);

    return () => {
      window.removeEventListener("BITS_3PT_MESSAGE_SIGNAL", handler);
    };
  }, [terminateEnabled, signalMechanism, effectiveSignalID, onTerminateSignal]);

  useEffect(() => {
    return () => {
      if (retryIntervalRef.current) {
        clearInterval(retryIntervalRef.current);
        retryIntervalRef.current = null;
      }
    };
  }, []);

  const disabled =
    !isAllowed(data) ||
    !isResponseArrived ||
    isRetryActive;

  const buttonLabel = isRetryActive
    ? `${data?.component?.sLabel} (${formatRetryTime(retrySecondsLeft)})`
    : data?.component?.sLabel;

  const withLoadingBoundary = content => (
    <ComponentLoadingBoundary
      loading={isLoading}
      label={`Loading ${data?.component?.sLabel || data?.component?.sName}`}
      minHeight={36}
      size={20}
    >
      {content}
    </ComponentLoadingBoundary>
  );

  if (
    (data?.component?.options.mode === "DEFAULT" ||
      data?.component?.options.mode === "LINK") &&
    data?.component?.sAdornType === ""
  ) {
    return withLoadingBoundary(
      <Button
        id={data.component.sName}
        onClick={handleButtonClick}
        {...data?.component?.sProps}
        aria-label={data?.component?.sHelper}
        disabled={disabled}
      >
        {buttonLabel}
      </Button>
    );
  }

  if (
    (data?.component?.options.mode === "DEFAULT" ||
      data?.component?.options.mode === "LINK") &&
    data?.component?.sAdornType === "Icon"
  ) {
    const Icons = MUIICon[data?.component?.sIcon];
    let startIcon = null;
    let endIcon = null;

    if (data?.component?.sAdornPosition === "Start") {
      startIcon = <Icons />;
    } else if (data?.component?.sAdornPosition === "End") {
      endIcon = <Icons />;
    }

    return withLoadingBoundary(
      <Button
        id={data.component.sName}
        onClick={handleButtonClick}
        {...data?.component?.sProps}
        aria-label={data?.component?.sHelper}
        startIcon={startIcon}
        endIcon={endIcon}
        disabled={disabled}
      >
        {buttonLabel}
      </Button>
    );
  }

  if (
    data?.component?.options.mode === "ICONBUTTON" &&
    data?.component?.sAdornType === "Icon"
  ) {
    return withLoadingBoundary(
      <IconButton
        onClick={handleButtonClick}
        {...data?.component?.sProps}
        disabled={disabled}
        aria-label={data?.component?.sHelper}
      >
        <Icon
          sProps={data?.component?.sIconProps}
          iconName={data?.component?.sIcon}
        />
      </IconButton>
    );
  }

  return "null";
};

export default memo(ButtonComponent);
